/**
 * Subtitle sync hook — phrase-level karaoke highlighting.
 *
 * Shows one sentence at a time. Within that sentence, highlights
 * 2-3 words as a phrase group that slides forward with playback.
 * Uses Polly speech marks for timing.
 */

import { useEffect, useRef, useState, useMemo } from "react";
import type { SpeechMark } from "@amzn/a4k-creator-studio-shared";

export type { SpeechMark };

interface UseSubtitlesOptions {
  speechMarks: SpeechMark[];
  isPlaying: boolean;
  isPaused: boolean;
}

export interface SubtitleState {
  activeSentence: string;
  words: string[];
  /** Start index of the currently highlighted phrase */
  phraseStart: number;
  /** End index (exclusive) of the currently highlighted phrase */
  phraseEnd: number;
  /** Progress 0-1 within the current phrase (for smooth sliding animation) */
  phraseProgress: number;
  visible: boolean;
}

const PHRASE_SIZE = 3;
const POLL_INTERVAL_MS = 50;

export function useSubtitles({ speechMarks, isPlaying, isPaused }: UseSubtitlesOptions): SubtitleState {
  const [activeSentenceIndex, setActiveSentenceIndex] = useState(-1);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [phraseProgress, setPhraseProgress] = useState(0);
  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(0);

  const sentenceMarks = useMemo(() => speechMarks.filter((m) => m.type === "sentence"), [speechMarks]);
  const wordMarks = useMemo(() => speechMarks.filter((m) => m.type === "word"), [speechMarks]);

  // Reset timing when speech marks change (new page)
  const prevMarksRef = useRef(speechMarks);
  if (prevMarksRef.current !== speechMarks) {
    prevMarksRef.current = speechMarks;
    startTimeRef.current = 0;
    pausedAtRef.current = 0;
  }

  useEffect(() => {
    if (!isPlaying || isPaused || speechMarks.length === 0) {
      if (isPaused) {
        // Paused — freeze subtitle in place, save elapsed for resume
        if (startTimeRef.current > 0) {
          pausedAtRef.current = Date.now() - startTimeRef.current;
          startTimeRef.current = 0;
        }
      } else if (!isPlaying) {
        // Fully stopped — reset everything
        setActiveSentenceIndex(-1);
        setActiveWordIndex(-1);
        setPhraseProgress(0);
        startTimeRef.current = 0;
        pausedAtRef.current = 0;
      }
      return;
    }

    startTimeRef.current = Date.now() - pausedAtRef.current;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;

      let sIdx = -1;
      for (let i = 0; i < sentenceMarks.length; i++) {
        if (sentenceMarks[i].time <= elapsed) sIdx = i;
        else break;
      }
      setActiveSentenceIndex(sIdx);

      if (sIdx >= 0) {
        const sentStart = sentenceMarks[sIdx].start;
        const sentEnd = sentenceMarks[sIdx].end;
        const sentenceWords = wordMarks.filter(
          (w) => w.start >= sentStart && w.end <= sentEnd
        );
        let wIdx = -1;
        for (let i = 0; i < sentenceWords.length; i++) {
          if (sentenceWords[i].time <= elapsed) wIdx = i;
          else break;
        }
        setActiveWordIndex(wIdx);

        // Compute smooth progress within the current word toward the next
        if (wIdx >= 0 && wIdx < sentenceWords.length - 1) {
          const currentTime = sentenceWords[wIdx].time;
          const nextTime = sentenceWords[wIdx + 1].time;
          const duration = nextTime - currentTime;
          if (duration > 0) {
            setPhraseProgress(Math.min(1, (elapsed - currentTime) / duration));
          }
        } else {
          setPhraseProgress(1);
        }
      } else {
        setActiveWordIndex(-1);
        setPhraseProgress(0);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isPlaying, isPaused, speechMarks, sentenceMarks, wordMarks]);

  const activeSentence = activeSentenceIndex >= 0 && activeSentenceIndex < sentenceMarks.length
    ? sentenceMarks[activeSentenceIndex].value : "";
  const words = useMemo(() => {
    if (!activeSentence) return [];
    return activeSentence.split(/(\s+)/).filter((w) => w.trim().length > 0);
  }, [activeSentence]);

  // Phrase window: highlight a group of PHRASE_SIZE words centered around activeWordIndex
  const phraseStart = Math.max(0, activeWordIndex);
  const phraseEnd = Math.min(words.length, phraseStart + PHRASE_SIZE);

  return {
    activeSentence,
    words,
    phraseStart,
    phraseEnd,
    phraseProgress,
    visible: (isPlaying || isPaused) && activeSentenceIndex >= 0,
  };
}
